import { useMemo } from "react"
import { useDebouncedValue } from "@/v2-ui/utils/utils.debounce"
import {
  CRM_LOOKUP_DEBOUNCE_MS,
  MATCH_KIND_EMAIL,
  MATCH_SOURCE_BACKOFFICE,
} from "@/v2-backoffice-client/crm/crm.constants"
import {
  isCompleteEmail,
  normalizeEmail,
} from "@/v2-backoffice-client/crm/crm.utils"
import { useContactsByEmailsQuery } from "@/v2-backoffice-client/crm/crm.contactsByEmails.query"
import type { ContactMatchCandidateFragment } from "@/v2-backoffice-client/crm/__types__/crm.contactMatch.fragment"

/** What the check found for one email. Both absent = brand new. */
export type ContactEmailCheck = {
  /** An app user already has this email (open it, don't create another). */
  existingUser?: ContactMatchCandidateFragment
  /** A HubSpot contact has this email (will be linked on create). */
  contact?: ContactMatchCandidateFragment
}

export type ContactEmailChecks = {
  /** Keyed by normalised email; only complete emails are checked. */
  byEmail: Map<string, ContactEmailCheck>
  /** Complete emails the current answer covers. */
  checkedEmails: Set<string>
  loading: boolean
  /** False until the first answer arrives (rows show a spinner meanwhile). */
  answered: boolean
  hubspotSearched: boolean
  /** Of the checked emails: how many are new to both systems. */
  newCount: number
  /** Of the checked emails: how many already belong to an app user. */
  existingUserCount: number
  /** Of the checked emails: how many are HubSpot contacts (and not users). */
  existingContactCount: number
}

const KEY_SEPARATOR = "\u0000"

/**
 * The bulk forms' live check: every complete email in the table, one
 * batched request per debounce tick, kept on screen while the next answer
 * loads. Rows read their own state from `byEmail`; the footer reads the
 * counts to word the "create missing contacts" checkbox.
 */
export function useContactEmailChecks(emails: string[]): ContactEmailChecks {
  const key = useDebouncedValue(
    [...new Set(emails.map(normalizeEmail).filter(isCompleteEmail))].join(
      KEY_SEPARATOR,
    ),
    CRM_LOOKUP_DEBOUNCE_MS,
  )
  const checkedEmails = useMemo(
    () => new Set(key ? key.split(KEY_SEPARATOR) : []),
    [key],
  )

  const { data, previousData, loading } = useContactsByEmailsQuery({
    skip: checkedEmails.size === 0,
    variables: { emails: [...checkedEmails] },
  })
  const result = (data ?? previousData)?.findContactsByEmails

  return useMemo(() => {
    const byEmail = new Map<string, ContactEmailCheck>()
    if (checkedEmails.size > 0) {
      for (const candidate of result?.Candidates ?? []) {
        if (candidate.MatchKind !== MATCH_KIND_EMAIL) continue
        const email = normalizeEmail(candidate.Email ?? "")
        if (!checkedEmails.has(email)) continue
        const check = byEmail.get(email) ?? {}
        if (candidate.Source === MATCH_SOURCE_BACKOFFICE) {
          // Live users outrank deleted ones when both share the email.
          if (!check.existingUser || check.existingUser.IsDeletedUser) {
            check.existingUser = candidate
          }
        } else {
          check.contact = candidate
        }
        byEmail.set(email, check)
      }
    }
    let newCount = 0
    let existingUserCount = 0
    let existingContactCount = 0
    for (const email of checkedEmails) {
      const check = byEmail.get(email)
      if (check?.existingUser) existingUserCount++
      else if (check?.contact) existingContactCount++
      else newCount++
    }
    return {
      byEmail,
      checkedEmails,
      loading,
      answered: result !== undefined,
      hubspotSearched: result?.HubspotSearched ?? true,
      newCount,
      existingUserCount,
      existingContactCount,
    }
  }, [checkedEmails, result, loading])
}
